/**
 * App-wide SINGLE camera stream.
 *
 * getUserMedia is expensive (camera negotiation + first-frame latency), and
 * opening a SECOND stream while one is already live causes OS-level contention
 * — on mobile the new stream comes back black or dark (see FOC-123). So instead
 * of release-and-re-acquire on every scan, we acquire the camera ONCE, cache
 * it, and reuse the same MediaStream across the Home permission check and every
 * /scan visit (including scan -> artwork -> scan again).
 *
 * Result: no cold-acquire pause and no blurred-background flash on repeat
 * scans, and — because there is only ever one stream, never two — no contention.
 *
 * Constraints match what the scanner needs (environment-facing, 1920² ideal,
 * >=24fps) so the Home accessibility check reflects the real scan requirement
 * AND the warmed stream is full-resolution for the reticle crop (FOC-114). The
 * engine downsamples every query's short edge to 256², so 1920² only matters
 * for small/distant-work reticles — exactly FOC-114's target set.
 *
 * The stream is released explicitly at the scan-flow boundary — returning to
 * Home (a fresh start) or the page unloading — NOT on Camera unmount, so it
 * survives the scan<->artwork navigation loop and stays reusable.
 */

const SCAN_VIDEO_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: "environment",
    width: { ideal: 1920 },
    height: { ideal: 1920 },
    frameRate: { ideal: 30, min: 24 },
  },
};

let current: MediaStream | null = null;

/** True only if the cached stream still has a live video track. */
const isLive = (stream: MediaStream | null): boolean =>
  !!stream && stream.getVideoTracks().some((t) => t.readyState === "live");

/**
 * Return the shared camera stream, reusing the live one if present, otherwise
 * acquiring (and caching) a fresh one. Rejects exactly like getUserMedia so
 * callers can surface a permission/availability error.
 */
export const acquireCameraStream = async (): Promise<MediaStream> => {
  if (isLive(current)) {
    return current as MediaStream;
  }
  // Stale (tracks ended, e.g. OS reclaim/backgrounding) — drop before re-acquire.
  releaseCameraStream();
  current = await navigator.mediaDevices.getUserMedia(SCAN_VIDEO_CONSTRAINTS);
  return current;
};

/**
 * Stop every track and clear the shared stream. Call at the scan-flow boundary
 * (returning Home, app reset, page unload) — NOT on Camera unmount, which must
 * leave the stream live for reuse on the next scan.
 */
export const releaseCameraStream = (): void => {
  if (current) {
    current.getTracks().forEach((t) => t.stop());
    current = null;
  }
};
